import { Button, Stack, Typography, useTheme } from "@mui/material";
import { Plus, Users } from "lucide-react";
import { useState } from "react";
import {
	type ArrayPath,
	type Control,
	type FieldArray,
	type FieldPath,
	type FieldValues,
	useFieldArray,
	useWatch,
} from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CoDebtorDrawer } from "web_myplann/Portfolio/Forms/CoDebtors/CoDebtorDrawer";
import {
	type CoDebtorFormValue,
	EMPTY_CO_DEBTOR,
} from "web_myplann/Portfolio/Forms/CoDebtors/coDebtorsSchema";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { joinTruthy } from "web_myplann/Shared/Utils/textFormatters";

const PageResources = Resources.Portfolio.CoDebtors;

const NEW_CO_DEBTOR = "new";

type CoDebtorsForm = FieldValues & {
	coDebtors: CoDebtorFormValue[];
};

type Props<FormType extends CoDebtorsForm, TTransformed extends FieldValues> = {
	control: Control<FormType, unknown, TTransformed>;
	sectionLabel: string;
};

export const CoDebtorsSection = <
	FormType extends CoDebtorsForm,
	TTransformed extends FieldValues,
>({
	control,
	sectionLabel,
}: Props<FormType, TTransformed>) => {
	const theme = useTheme();
	const { t } = useResource();
	const [openedIndex, setOpenedIndex] = useState<
		number | typeof NEW_CO_DEBTOR | null
	>(null);

	const { fields, append, update, remove } = useFieldArray({
		control,
		name: "coDebtors" as ArrayPath<FormType>,
	});

	const coDebtors = useWatch({
		control,
		name: "coDebtors" as FieldPath<FormType>,
	}) as CoDebtorFormValue[] | undefined;

	const getOtherIdentifications = (excludedIndex?: number) =>
		(coDebtors ?? [])
			.filter((_, index) => index !== excludedIndex)
			.map((coDebtor) => coDebtor.identification);

	const handleSave = (coDebtor: CoDebtorFormValue) => {
		const value = coDebtor as FieldArray<FormType, ArrayPath<FormType>>;

		if (openedIndex === NEW_CO_DEBTOR) {
			append(value);
		} else if (openedIndex !== null) {
			update(openedIndex, value);
		}

		setOpenedIndex(null);
	};

	const handleRemove = (index: number) => {
		remove(index);
		setOpenedIndex(null);
	};

	return (
		<>
			<Typography variant="sectionLabel">{sectionLabel}</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				{fields.map((field, index) => (
					<ListRow
						key={field.id}
						title={joinTruthy(
							[coDebtors?.[index]?.firstname, coDebtors?.[index]?.lastname],
							" ",
						)}
						subtitle={coDebtors?.[index]?.identification}
						icon={
							<IconBox bg={theme.palette.blue[100]}>
								<Users
									size={theme.iconSize.md}
									color={theme.palette.blue.main}
								/>
							</IconBox>
						}
						showChevron
						onClick={() => setOpenedIndex(index)}
					/>
				))}
				<Button
					variant="inlineAction"
					startIcon={<Plus size={theme.iconSize.sm} />}
					onClick={() => setOpenedIndex(NEW_CO_DEBTOR)}
				>
					{t(PageResources.add)}
				</Button>
			</Stack>

			{openedIndex === NEW_CO_DEBTOR && (
				<CoDebtorDrawer
					coDebtor={EMPTY_CO_DEBTOR}
					otherIdentifications={getOtherIdentifications()}
					onSave={handleSave}
					onClose={() => setOpenedIndex(null)}
				/>
			)}
			{typeof openedIndex === "number" && coDebtors?.[openedIndex] && (
				<CoDebtorDrawer
					coDebtor={coDebtors[openedIndex]}
					otherIdentifications={getOtherIdentifications(openedIndex)}
					onSave={handleSave}
					onRemove={() => handleRemove(openedIndex)}
					onClose={() => setOpenedIndex(null)}
				/>
			)}
		</>
	);
};
